/**
 * VoyagePlanUnderwayCard.js — Sub-ticket C + Pass 2a
 *
 * The Master's primary plan interaction surface during UNDERWAY/ARRIVAL.
 * Renders the SNAPSHOT of the attached voyage plan at the top of the voyage
 * log, with one tappable row per snapshot item. NEVER reads from the live
 * voyage_settings.voyagePlans library — snapshot is the source of truth
 * (P4.6 snapshot-read-site rule).
 *
 * Pass 2a additions (per CODING-BRIEF-PASS-2A.md):
 *   - Phase markers (Pre-Departure, Let Go, Pre-Arrival, All Fast) interleaved
 *     with admin's plan items. Voyage-level sentinels, NOT plan items —
 *     never written to voyage.voyagePlanItems / voyage.voyagePlanSnapshot.items.
 *   - Completion timestamps on each plan-item row when ✓ Done / ✗ Skipped.
 *
 * Render branches (synthesis §5.2):
 *   resolution.status === 'attached'     → full card (sub-cases below)
 *   resolution.status === 'no_plan_selected' → null (normal "no plan" state)
 *   resolution.status === 'source_deleted'   → inline banner
 *   resolution.status === 'source_archived'  → inline banner
 *   resolution.status === 'empty_plan'       → inline banner
 *   resolution.status === 'vessel_changed'   → inline banner
 *
 *   attached + !snapshot                 → null (defensive)
 *   attached + snapshot.items empty      → full card (phase markers only, Pass 2a §1.6)
 *   attached + items                     → full card
 *
 * Backward compat: pre-B voyages have NO voyagePlanResolution / snapshot /
 * items fields. Inner guard returns null; outer parent also guards.
 *
 * Pre-arrival hard-filter (P4.9 final defence layer):
 *   A1 §4   covers WRITE via assertValidPlanItemKind + READ at the data hook
 *           via normalisePlanItems.
 *   A2     OperationRefPicker excludes pre_arrival from the picker.
 *   THIS   the LAST layer — even if a malformed snapshot somehow reaches the
 *          card, pre-arrival items are silently dropped + console.warn'd.
 *   FT-285 consolidated pre-arrival under the FAB Arrive button as the
 *   SINGLE regulatory entry point. Adding pre-arrival to a plan would
 *   regress that.
 *
 * Render-zero discipline (CORRECTION 3 from A1/A2/B lessons): every numeric
 * short-circuit MUST be `!!()` or ternary; counters use template strings.
 * See §5.3 in CODING-BRIEF-C.md AND §1.4 / §4.4 in Pass 2a brief.
 *
 * planItemId correlation (CODING-BRIEF-C §2.3 + §3.5): the persisted
 * voyagePlanItems[itemId].status is the primary source of truth; the
 * unifiedLog observer is BELT-AND-BRACES against the rare race where the
 * operation_complete entry landed but updatePlanItem didn't.
 *
 * Pass 2e additions (per CODING-BRIEF-PASS-2E.md):
 *   - Pre-Arr and All Fast phase markers become CONDITIONALLY tappable when
 *     they represent the next-action milestone. Pre-Arr tap fires the parent's
 *     handlePrepareForArrival (same handler the FAB Arrive button fires).
 *     All Fast tap scrolls + focuses the inline time field on ArrivalPhase.
 *   - Pre-Dep and Let Go remain decorative per Pass 2a §2A.
 *   - Conditional interactivity is driven by parent-passed onTapPhaseAction
 *     prop, NOT by a flag inside PhaseMarkerRow.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ClipboardList, MapPin, ClipboardCheck, ChevronDown, ChevronUp, MoreVertical, Undo2, Circle, CheckCircle2, MinusCircle } from 'lucide-react';
import { VOYAGE_STATUS } from '../../../../../config/operationsConfig';
import { formatDateInTimezone } from '../../../../../utils/timezoneUtils';

// Pass 2o: ad-hoc activity window. Triplicated predicate (anti-Pass-2b — do
// NOT extract). Mirrored in VoyageDocument + UnderwayPhase.
const ACTIVE_OPERATION_ADHOC_WINDOW_MS = 60 * 60 * 1000;  // 60 minutes
// eslint-disable-next-line complexity
function findActiveSection(log, planItems, nowMs) {
  const entries = log || [];
  const sections = entries.filter(e => e && e.type === 'section' && e.operationId);
  const completedIds = new Set(
    entries.filter(e => e && e.type === 'operation_complete' && e.operationId).map(e => e.operationId)
  );
  const items = planItems || {};
  for (const section of [...sections].reverse()) {
    if (completedIds.has(section.operationId)) continue;
    if (section.planItemId) {
      const itemStatus = items[section.planItemId]?.status;
      if (itemStatus && itemStatus !== 'completed' && itemStatus !== 'skipped') return section;
      if (itemStatus === 'completed' || itemStatus === 'skipped') continue;
      // itemStatus undefined: fall through to ad-hoc window
    }
    const lastActivityMs = entries.reduce((max, e) => {
      if (!e || e.type !== 'check' || e.operationId !== section.operationId) return max;
      const t = e.completedAt || e.skippedAt || null;
      if (!t) return max;
      const ms = new Date(t).getTime();
      return Number.isFinite(ms) && ms > max ? ms : max;
    }, 0);
    if (lastActivityMs > 0 && nowMs - lastActivityMs <= ACTIVE_OPERATION_ADHOC_WINDOW_MS) return section;
  }
  return null;
}

// ============================================================================
// Pass 2a — Phase marker module-scope constants + helpers.
//
// `[v5 FIX S2]` ALL helpers and constants MUST be at module scope above the
// component. Per FT-270 C.7/C.8 TDZ pattern. Per
// `feedback_runtime_smoke_test_before_done`.
// ============================================================================

// `[v5 FIX S1]` Use VOYAGE_STATUS enum, NOT string literals. Locks against
// future enum renames.
//
// pre_dep is "past" once any of these are reached, because Let Go gates on
// canLetGo (which itself gates on all pre-dep checks being resolved).
const PRE_DEP_PASSED_STATUSES = [
  VOYAGE_STATUS.UNDERWAY,
  VOYAGE_STATUS.ARRIVAL,
  VOYAGE_STATUS.COMPLETED
];

const PHASE_MARKERS = [
  { id: '__phase_pre_departure', kind: 'phase', label: 'Pre-Departure', placement: 'leading' },
  { id: '__phase_let_go',        kind: 'phase', label: 'Let Go',        placement: 'leading' },
  { id: '__phase_pre_arrival',   kind: 'phase', label: 'Pre-Arrival',   placement: 'trailing' },
  { id: '__phase_all_fast',      kind: 'phase', label: 'All Fast',      placement: 'trailing' }
];

function isValidISO(v) {
  return typeof v === 'string' && v.length > 0 && !Number.isNaN(Date.parse(v));
}

function latestCheckResolutionTimestamp(voyage) {
  // Defensive fallback: max across V2-shape unifiedLog check entries'
  // completedAt/skippedAt. Used ONLY when status is past pre-dep but
  // departureTime is somehow missing (should never happen in prod).
  const log = voyage?.unifiedLog || [];
  let latest = null;
  log.forEach(e => {
    if (!e || e.type !== 'check' || e.checkType !== 'pre_departure') return;
    const ts = e.completedAt || e.skippedAt || null;
    if (ts && (!latest || ts > latest)) latest = ts;
  });
  return latest;
}

function derivePreDepartureMarker(voyage) {
  // ─── STATUS SHORTCUT [v4 FIX Pre-Dep hybrid] ─────────────────────────
  // If the voyage has progressed past PRE_DEPARTURE, Pre-Dep is by
  // definition Done. This handles:
  //   (a) Legacy voyages where Let Go fired before V2 was deployed —
  //       no V2-shape unifiedLog entries exist, only synthetic
  //       legacy-shape entries written by letGo (which lack
  //       checkType/checkId and would otherwise be invisible to the
  //       hybrid predicate below).
  //   (b) Any future shape change to in-flight pre-dep storage — past
  //       Let Go, the voyage's status is the source of truth, not the
  //       check entries.
  // Returns departureTime as canonical timestamp; defensive fallback
  // to latest unifiedLog check resolution if departureTime is missing.
  if (PRE_DEP_PASSED_STATUSES.includes(voyage?.status)) {
    const ts = isValidISO(voyage?.departureTime)
      ? voyage.departureTime
      : latestCheckResolutionTimestamp(voyage);
    return { done: true, resolvedAt: ts };
  }

  // ─── PRE-DEPARTURE PHASE — HYBRID PREDICATE ──────────────────────────
  // `[v5 FIX B1]` Voyage is still in pre_departure. Compute Done from BOTH
  // shapes:
  //   - PRIMARY in-flight path: `preDepartureChecks[i].completed === true`
  //     (written by `updatePreDepartureCheck:1073-1118` — what the UI
  //     uses TODAY when the Master ticks a check).
  //   - SECONDARY (defensive): V2-shape unifiedLog `check` entries with
  //     matching `checkType`/`checkId` (present today only as initial-
  //     completion seeds from voyage-start at `:869-881`; future-proofs
  //     against `completeCheckV2` being wired for pre-dep checks later).
  // Either satisfies a check; both are read but never written.
  const checks = voyage?.preDepartureChecks || [];
  if (checks.length === 0) {
    return { done: false, resolvedAt: null };   // R1-C2 — empty array MUST NOT default to Done
  }

  const log = voyage?.unifiedLog || [];
  // Secondary path indexing: index V2-shape unifiedLog entries by checkId,
  // keep the entry that ACTUALLY resolved (completed OR skipped true).
  // Synthetic legacy-shape entries written by letGo lack checkType/checkId
  // and are naturally skipped here.
  const v2EntriesByCheckId = new Map();
  log.forEach(e => {
    if (!e || e.type !== 'check' || e.checkType !== 'pre_departure') return;
    if (typeof e.checkId !== 'string') return;
    if (!(e.completed === true || e.skipped === true)) return;
    const existing = v2EntriesByCheckId.get(e.checkId);
    const ts = e.completedAt || e.skippedAt || null;
    if (!existing) {
      v2EntriesByCheckId.set(e.checkId, { entry: e, ts });
    } else if (ts && existing.ts && ts > existing.ts) {
      v2EntriesByCheckId.set(e.checkId, { entry: e, ts });
    }
  });

  let latestTs = null;
  const allResolved = checks.every(c => {
    // Secondary path: matching V2-shape unifiedLog entry with completed or
    // skipped set (today: only voyage-start seed entries hit this branch).
    const v2Entry = v2EntriesByCheckId.get(c.id);
    if (v2Entry) {
      if (v2Entry.ts && (!latestTs || v2Entry.ts > latestTs)) {
        latestTs = v2Entry.ts;
      }
      return true;
    }
    // `[v5 FIX B1]` PRIMARY in-flight path: preDepartureChecks[i].completed
    // === true. Written by updatePreDepartureCheck:1073-1118 — the handler
    // the UI fires every time the Master ticks a pre-dep check today (called
    // from PreDeparturePhase.js:114, 276). Skip status NEVER appears on
    // preDepartureChecks[i] in any path (no skip menu on pre-dep checks
    // today), so checking c.completed is sufficient here.
    if (c.completed === true) {
      const ts = c.completedAt;
      if (isValidISO(ts) && (!latestTs || ts > latestTs)) {
        latestTs = ts;
      }
      return true;
    }
    return false;
  });

  if (!allResolved) return { done: false, resolvedAt: null };
  return {
    done: true,
    resolvedAt: latestTs || voyage?.departureTime || null   // fallback only if no per-check ts
  };
}

function deriveLetGoMarker(voyage) {
  const done = isValidISO(voyage?.departureTime);
  return {
    done,
    resolvedAt: done ? voyage.departureTime : null
  };
}

function derivePreArrivalMarker(voyage) {
  const log = voyage?.unifiedLog || [];
  const sections = log.filter(e =>
    e && e.type === 'section' && e.operationType === 'pre_arrival' && e.operationId
  );
  if (sections.length === 0) return { done: false, resolvedAt: null };
  const sectionIds = new Set(sections.map(s => s.operationId));
  // Latest completion wins (FT-285 may produce multiple). [...log].reverse().find(...)
  // is `findLast`-equivalent and portable to older bundler targets.
  const completion = [...log].reverse().find(e =>
    e && e.type === 'operation_complete' && sectionIds.has(e.operationId)
  );
  if (!completion) return { done: false, resolvedAt: null };
  return {
    done: true,
    resolvedAt: completion.timestamp || completion.eventTime || null
  };
}

function deriveAllFastMarker(voyage) {
  const done = isValidISO(voyage?.arrivalTime);
  return {
    done,
    resolvedAt: done ? voyage.arrivalTime : null
  };
}

// ============================================================================
// Inline status banner — non-attached resolution states.
// ============================================================================

const STATUS_BANNER_COPY = {
  source_deleted: 'The Voyage Plan attached to this voyage has been deleted. Voyage proceeds without plan.',
  source_archived: 'The Voyage Plan attached to this voyage has been archived. Voyage proceeds without plan.',
  empty_plan: 'The Voyage Plan attached to this voyage has no items. Voyage proceeds without plan.',
  vessel_changed: 'The Voyage Plan attached to this voyage no longer belongs to this vessel. Voyage proceeds without plan.'
};

const VoyagePlanInlineBanner = ({ status }) => {
  const copy = STATUS_BANNER_COPY[status];
  if (!copy) return null;
  return (
    <div
      className="mx-3 sm:mx-4 mt-3 mb-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      role="status"
      data-testid="voyage-plan-banner"
      data-status={status}
    >
      <span aria-hidden="true">⚠️ </span>
      {copy}
    </div>
  );
};

// ============================================================================
// Single-item skip modal (small purpose-built; see CODING-BRIEF-C §5.6 —
// BulkSkipReasonModal's title copy "Some checks were not completed" does NOT
// degrade cleanly to single-plan-item skip).
// ============================================================================

const SinglePlanItemSkipModal = ({ isOpen, item, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const trimmed = reason.trim();
  const canConfirm = trimmed.length >= 3;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm?.(trimmed);
  };

  const itemLabel = item.kind === 'waypoint'
    ? (item.label || 'Waypoint')
    : (item.operationSnapshot?.name || item.displayLabel || 'Operation');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="plan-item-skip-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 bg-gray-900 text-white">
          <h2 className="text-lg font-semibold">Skip plan item</h2>
          <p className="text-sm text-slate-300 mt-1">
            &ldquo;{itemLabel}&rdquo; will be marked skipped.
          </p>
        </div>
        <div className="p-4 space-y-3">
          <label className="block text-sm font-medium text-slate-700">
            Reason
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Why is this item being skipped?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/30"
          />
          <p className="text-xs text-slate-500">Minimum 3 characters.</p>
        </div>
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200 min-h-[44px]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            Skip item
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Undo snackbar — co-located 5-second floating action.
// ============================================================================

const UndoSnackbar = ({ message, onUndo, onDismiss }) => {
  return (
    <div
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-40 max-w-md w-[92%] sm:w-auto"
      role="status"
      data-testid="plan-item-undo-snackbar"
    >
      <div className="bg-slate-900 text-white rounded-xl shadow-lg px-4 py-3 flex items-center justify-between gap-3">
        <span className="text-sm">{message}</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onUndo}
            className="flex items-center gap-1 text-sm font-semibold text-amber-300 hover:text-amber-200 min-h-[40px] px-2"
          >
            <Undo2 className="h-4 w-4" />
            Undo
          </button>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="text-slate-400 hover:text-white px-1"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Status badge for an item row.
// Pass 2a §2.4 — accepts optional timeText + itemId; renders inline timestamp
// when truthy, per-item testid prevents collisions across multi-item tests.
// ============================================================================

const STATUS_BADGE = {
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'In progress', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  completed: { label: 'Done', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  skipped: { label: 'Skipped', cls: 'bg-amber-50 text-amber-800 border-amber-200' }
};

// ============================================================================
// Pass 2c §1.5 — Plan item + phase marker status visual mappings.
//
// LEFT-SIDE STATUS ICON + LABEL CLASS PER ROW STATE. Plan items have 4 states
// (pending, in_progress, completed, skipped). Phase markers have 2 (done true
// /false). Skipped rows additionally get `line-through` on the label.
//
// MODULE SCOPE — TDZ-safe per FT-270 C.7/C.8 and Pass 2a [v5 FIX S2].
//
// The right-side status badge (STATUS_BADGE above) is UNCHANGED — Pass 2c is
// purely additive to the left side of each row.
//
// [v2 FIX S2] The `in_progress` arm is DEFENSIVE. `derivedStatusByItemId` at
// VoyagePlanUnderwayCard.js:622-644 currently returns only 'pending' |
// 'completed' | 'skipped' — 'in_progress' is unreachable today. The visual
// mapping is included so future derivation changes don't fall through to
// undefined. Group V test Z5-PASS-2C asserts unknown-status fallback to pending.
// ============================================================================

const PLAN_ITEM_STATUS_VISUAL = {
  pending:     { Icon: Circle,       iconClass: 'text-slate-400',   labelClass: 'text-slate-900',   strike: false },
  in_progress: { Icon: Circle,       iconClass: 'text-sky-500',     labelClass: 'text-sky-700',     strike: false },
  completed:   { Icon: CheckCircle2, iconClass: 'text-emerald-600', labelClass: 'text-emerald-700', strike: false },
  skipped:     { Icon: MinusCircle,  iconClass: 'text-amber-600',   labelClass: 'text-amber-700',   strike: true  }
};

const PHASE_MARKER_VISUAL = {
  false: { Icon: Circle,       iconClass: 'text-slate-400',   labelClass: 'text-slate-800' },
  true:  { Icon: CheckCircle2, iconClass: 'text-emerald-600', labelClass: 'text-emerald-700' }
};

const PlanItemStatusBadge = ({ status, timeText, itemId }) => {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${cfg.cls}`}
      data-testid={`plan-item-status-${status}`}
    >
      <span>{cfg.label}</span>
      {!!timeText && (
        <span
          className="font-mono ml-1"
          data-testid={`plan-item-resolved-at-${itemId}`}
        >
          {timeText}
        </span>
      )}
    </span>
  );
};

// ============================================================================
// Single-item row — 56pt min-height per CODING-BRIEF-C §5.4.
// Pass 2a §2.4 — extended with completedAt + vesselTimezone props, computes
// time string per §2.2 unified timezone fallback chain.
// ============================================================================

const PlanItemRow = ({
  item,
  voyage,
  status,
  isReadOnly,
  onTap,
  onRequestSkip,
  completedAt,
  vesselTimezone
}) => {
  // P4.6 — read display label from SNAPSHOT, never from live operationsList.
  const label = item.kind === 'waypoint'
    ? (item.label || 'Waypoint')
    : (item.operationSnapshot?.name || item.displayLabel || 'Operation');

  // Lucide icons matched to the synthesis brief 📍 / 📋 affordances.
  const Icon = item.kind === 'waypoint' ? MapPin : ClipboardCheck;
  const iconColor = item.kind === 'waypoint' ? 'text-sky-600' : 'text-slate-700';

  const terminal = status === 'completed' || status === 'skipped';
  const interactive = !isReadOnly && !terminal;

  // Pass 2a §2.2 — unified timezone fallback chain; render time only when
  // terminal AND a valid ISO is present.
  const tz = voyage?.preferredTimezone || vesselTimezone || 'UTC';
  const timeText = (completedAt && terminal)
    ? formatDateInTimezone(completedAt, tz, { format: 'time' })
    : '';

  // Pass 2c §1.6 — status visual lookup (left-side icon + label class + strike).
  // Defensive fallback to `pending` if status is somehow unknown — prevents a
  // crash on a future state value.
  const statusVisual = PLAN_ITEM_STATUS_VISUAL[status] || PLAN_ITEM_STATUS_VISUAL.pending;
  const StatusIcon = statusVisual.Icon;

  return (
    <div
      className={`flex items-center gap-3 px-3 sm:px-4 py-3 min-h-[56px] border-t border-slate-100 transition-colors ${interactive ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
      onClick={interactive ? () => onTap?.(item) : undefined}
      role={interactive ? 'button' : 'group'}
      tabIndex={interactive ? 0 : -1}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap?.(item); } } : undefined}
      data-testid={`plan-item-row-${item.id}`}
      data-kind={item.kind}
      data-status={status}
    >
      <StatusIcon
        className={`h-5 w-5 flex-shrink-0 pointer-events-none ${statusVisual.iconClass}`}
        aria-hidden="true"
        data-testid={`plan-item-status-icon-${item.id}`}
      />
      <Icon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${statusVisual.labelClass} ${statusVisual.strike ? 'line-through' : ''}`}>{label}</p>
        {item.kind === 'waypoint' && item.instructionNote ? (
          <p className="text-xs text-slate-500 truncate">{item.instructionNote}</p>
        ) : null}
      </div>
      <PlanItemStatusBadge status={status} timeText={timeText} itemId={item.id} />
      {interactive ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRequestSkip?.(item); }}
          aria-label={`Skip ${label}`}
          className="p-2 rounded-full hover:bg-slate-200 min-h-[40px] min-w-[40px] flex items-center justify-center text-slate-500"
          data-testid={`plan-item-skip-${item.id}`}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};

// ============================================================================
// Pass 2a + Pass 2e — Phase marker row.
//
// §2A spec — voyage-level sentinel. CONDITIONALLY INTERACTIVE per Pass 2e:
// when the parent passes an `onTapPhaseAction` callback, the row becomes
// tappable with full keyboard support. When omitted (Pre-Dep, Let Go,
// non-actionable Pre-Arr/All Fast states), the row stays decorative.
//
// Pass 2c — Lucide `Circle`/`CheckCircle2` driven by PHASE_MARKER_VISUAL
// (replaces the previous amber `Flag` icon).
//
// Pass 2e [LOCK REVERSED] — Pre-Dep and Let Go still NEVER receive a handler.
// Only Pre-Arr and All Fast become interactive, and only when their gating
// booleans (preArrTappable / allFastTappable) are true. Tappability flows
// from the parent's per-marker logic, not from any field on the marker row.
// ============================================================================

const PhaseMarkerRow = ({ id, label, done, resolvedAt, vesselTimezone, voyage, onTapPhaseAction }) => {
  const tz = voyage?.preferredTimezone || vesselTimezone || 'UTC';
  const timeText = (done && resolvedAt)
    ? formatDateInTimezone(resolvedAt, tz, { format: 'time' })
    : '';
  const slug = id.replace('__phase_', '').replace(/_/g, '-');
  // produces e.g. plan-phase-row-pre-departure

  // Pass 2c §1.7 — phase marker status visual lookup.
  // Phase markers have 2 states (done true/false) — narrower than plan items.
  // Pass 2c REPLACES the existing Flag icon with a status-driven Circle /
  // CheckCircle2. Locked decision #8: no "Flag plus status icon" layering.
  const phaseVisual = PHASE_MARKER_VISUAL[done] || PHASE_MARKER_VISUAL[false];
  const PhaseStatusIcon = phaseVisual.Icon;

  // Pass 2e §1.2 [Pass 2e LOCK REVERSED] — interactive when caller passes a
  // handler. Caller (VoyagePlanUnderwayCardInner) only passes a handler for
  // Pre-Arr (when preArrTappable) and All Fast (when allFastTappable). For
  // Pre-Dep / Let Go (always) and for Pre-Arr / All Fast when the gating
  // conditions are not met, onTapPhaseAction is undefined → row is decorative.
  // [Pass 2e LOCKED — anti-Pass-2b] Kept the <div> wrapper to preserve the
  // existing badge flex layout — do NOT swap to <button>. PlanItemRow at :467
  // is the proven precedent: same pattern, same affordance.
  const interactive = typeof onTapPhaseAction === 'function';

  return (
    <div
      className={`flex items-center gap-2 px-3 sm:px-4 py-2 border-t border-slate-100 transition-colors ${interactive ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
      onClick={interactive ? onTapPhaseAction : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTapPhaseAction(); } } : undefined}
      aria-label={interactive ? `${label} — tap to action` : undefined}
      data-testid={`plan-phase-row-${slug}`}
      data-done={done ? 'true' : 'false'}
      data-interactive={interactive ? 'true' : 'false'}
    >
      <PhaseStatusIcon
        className={`h-5 w-5 flex-shrink-0 pointer-events-none ${phaseVisual.iconClass}`}
        aria-hidden="true"
        data-testid={`plan-phase-status-icon-${slug}`}
      />
      <span className={`flex-1 text-sm font-medium truncate ${phaseVisual.labelClass}`}>{label}</span>
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${
          done
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-slate-200 bg-slate-50 text-slate-600'
        }`}
        data-testid={`plan-phase-status-${slug}`}
      >
        <span>{done ? 'Done' : 'Pending'}</span>
        {!!timeText && (
          <span className="font-mono ml-1">{timeText}</span>
        )}
      </span>
    </div>
  );
};

// ============================================================================
// Main card component.
// ============================================================================

const VoyagePlanUnderwayCard = ({
  voyage,
  onUpdatePlanItem,
  onSkipPlanItem,
  onLogPositionForWaypoint,
  onStartOperationForPlanItem,
  vesselTimezone,
  onTapPreArrival,   // Pass 2e — fired when Pre-Arr marker is tapped (only when preArrTappable).
  onTapAllFast,      // Pass 2e — fired when All Fast marker is tapped (only when allFastTappable).
  onRefusedDuplicateTap   // Pass 2t — fired when a duplicate-section tap is refused so parent can show toast.
}) => {
  // ────────────────────────────────────────────────────────────────────────
  // Backward compatibility: voyages predating Sub-ticket B have no
  // voyagePlanResolution. Returning null here is the deliberate no-op —
  // pre-B voyages must render exactly as they did before C, with NO new UI
  // of any kind. The parent (UnderwayPhase) also wraps this in an outer
  // `voyage?.voyagePlanResolution && ...` guard (belt-and-braces).
  // ────────────────────────────────────────────────────────────────────────
  const resolution = voyage?.voyagePlanResolution;
  if (!resolution) return null;

  const { status: resolutionStatus } = resolution;

  // Non-attached statuses → banner only (or null for the normal no-plan state).
  if (resolutionStatus === 'no_plan_selected') return null;
  if (resolutionStatus !== 'attached') {
    return <VoyagePlanInlineBanner status={resolutionStatus} />;
  }

  // Attached but no snapshot or non-array items → render nothing.
  // P4.6 — snapshot is the source of truth, NEVER live voyage_settings.voyagePlans.
  //
  // Pass 2a §1.6 — empty items array NO LONGER returns null here; the inner
  // component renders phase markers even when admin items are zero. The
  // sortedItems pipeline handles empty arrays cleanly.
  const snapshot = voyage.voyagePlanSnapshot;
  if (!snapshot || !Array.isArray(snapshot.items)) {
    return null;
  }

  // The full card is gated above. Delegate to a render component so the inner
  // hooks (useState / useMemo / useEffect / useCallback) are called
  // UNCONDITIONALLY for Rules-of-Hooks compliance. Returning before any of the
  // above hooks would otherwise violate the rule on prop changes.
  return (
    <VoyagePlanUnderwayCardInner
      voyage={voyage}
      snapshot={snapshot}
      onUpdatePlanItem={onUpdatePlanItem}
      onSkipPlanItem={onSkipPlanItem}
      onLogPositionForWaypoint={onLogPositionForWaypoint}
      onStartOperationForPlanItem={onStartOperationForPlanItem}
      vesselTimezone={vesselTimezone}
      onTapPreArrival={onTapPreArrival}     // Pass 2e
      onTapAllFast={onTapAllFast}           // Pass 2e
      onRefusedDuplicateTap={onRefusedDuplicateTap}   // Pass 2t
    />
  );
};

const VoyagePlanUnderwayCardInner = ({
  voyage,
  snapshot,
  onUpdatePlanItem,
  onSkipPlanItem,
  onLogPositionForWaypoint,
  onStartOperationForPlanItem,
  vesselTimezone,
  onTapPreArrival,      // Pass 2e — fired when Pre-Arr marker is tapped (only when preArrTappable).
  onTapAllFast,         // Pass 2e — fired when All Fast marker is tapped (only when allFastTappable).
  onRefusedDuplicateTap // Pass 2t — fired when a duplicate-section tap is refused.
}) => {
  // ────────────────────────────────────────────────────────────────────────
  // Read-only gate: once the voyage has ALL FAST'd (status !== UNDERWAY/ARRIVAL,
  // or a milestone:all_fast lives in the log), plan-item taps are no-ops.
  // The underlying hook also blocks on status (defence in depth) but the
  // UI should not invite taps when the voyage has ended.
  // ────────────────────────────────────────────────────────────────────────
  const isReadOnly = useMemo(() => {
    if (!voyage) return true;
    const log = voyage.unifiedLog || [];
    const allFastFired = log.some(e => e.type === 'milestone' && e.milestoneType === 'all_fast');
    return allFastFired || voyage.status === 'completed' || voyage.status === 'cancelled' || voyage.status === 'aborted';
  }, [voyage]);

  // ────────────────────────────────────────────────────────────────────────
  // Sort + pre-arrival hard filter (P4.9 final defence layer — see file head).
  // ────────────────────────────────────────────────────────────────────────
  const sortedItems = useMemo(
    () => [...snapshot.items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [snapshot.items]
  );

  const renderableItems = useMemo(() => sortedItems.filter(it => {
    // Sub-ticket C — P4.9 final defence layer for pre-arrival.
    // FT-285 consolidated pre-arrival under the FAB Arrive button as the
    // SINGLE regulatory entry point. Adding pre-arrival to a plan would
    // regress that.
    if (it.kind === 'operation_ref' && it.operationId === 'pre_arrival') {
      // eslint-disable-next-line no-console
      console.warn(
        '[VoyagePlanUnderwayCard] dropping pre_arrival operation-ref from plan render — P4.9.',
        { itemId: it.id }
      );
      return false;
    }
    return true;
  }), [sortedItems]);

  // ────────────────────────────────────────────────────────────────────────
  // Derived status per item (synthesis §3.5).
  // Persisted voyagePlanItems[itemId].status is primary source of truth.
  // unifiedLog observer is belt-and-braces against a rare race where the
  // operation_complete entry landed but updatePlanItem didn't.
  // P4.6 — voyagePlanItems read site (snapshot derivative); NEVER reaches
  // back to voyage_settings.voyagePlans.
  //
  // `[v3 FIX R1-C3]` LOCKED: iterates `renderableItems` ONLY (NOT
  // `itemsWithPhaseMarkers`). Phase marker status lives in the separate
  // `phaseMarkerStatus` memo — phase marker ids are NOT keys in
  // voyage.voyagePlanItems and would falsely depress the doneItems counter.
  // ────────────────────────────────────────────────────────────────────────
  const derivedStatusByItemId = useMemo(() => {
    const items = voyage?.voyagePlanItems || {};
    const log = voyage?.unifiedLog || [];
    const completedPlanItemIds = new Set(
      log
        .filter(e => e && e.type === 'operation_complete' && e.planItemId)
        .map(e => e.planItemId)
    );
    const out = {};
    for (const it of renderableItems) {
      const persisted = items[it.id]?.status || 'pending';
      if (persisted === 'completed' || persisted === 'skipped') {
        out[it.id] = persisted;
        continue;
      }
      if (it.kind === 'operation_ref' && completedPlanItemIds.has(it.id)) {
        out[it.id] = 'completed';
        continue;
      }
      out[it.id] = persisted;
    }
    return out;
  }, [renderableItems, voyage?.voyagePlanItems, voyage?.unifiedLog]);

  // ────────────────────────────────────────────────────────────────────────
  // Pass 2a — Phase marker status derivation. Reads voyage-record fields only.
  //
  // MEMO-SAFETY NOTE: every write site in useVoyages.js produces a NEW array
  // reference for unifiedLog (spread or .map). Verified at brief-write time:
  //   - spread writes at :1297, :1425, :1611, :2662, :2702, :2743, :2781,
  //     :2828, :2878, :2974, :3236, :3478, :3742
  //   - .map writes at :1822, :1953, :1984, :3271, :3311, :3463
  // Therefore depending on voyage.unifiedLog by identity is safe; the memo
  // will refresh on every mutation. DO NOT add `voyage` itself as a dep —
  // that re-renders on every minor voyage-record change.
  //
  // `[v5 FIX B2]` `voyage?.status` MUST be in the dep array because the
  // status shortcut in derivePreDepartureMarker reads it. Without the dep,
  // status-only PRE_DEPARTURE → UNDERWAY transitions may not re-trigger
  // derivation.
  // ────────────────────────────────────────────────────────────────────────
  const phaseMarkerStatus = useMemo(() => ({
    pre_departure: derivePreDepartureMarker(voyage),
    let_go:        deriveLetGoMarker(voyage),
    pre_arrival:   derivePreArrivalMarker(voyage),
    all_fast:      deriveAllFastMarker(voyage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    voyage?.status,
    voyage?.preDepartureChecks,
    voyage?.departureTime,
    voyage?.arrivalTime,
    voyage?.unifiedLog
  ]);

  // ────────────────────────────────────────────────────────────────────────
  // Pass 2e §1.1 — derive tappable booleans for the two interactive markers.
  // Pre-Arr: tappable when voyage is UNDERWAY, the Pre-Arr marker is NOT yet
  //          done, and NO operation is currently active (mirrors
  //          FloatingActionBar.js:105 `disabled: hasActiveOperation ||
  //          preArrivalDone` so the marker tap stays coherent with the FAB
  //          lock — no double-fire surface).
  // All Fast: tappable when voyage is ARRIVAL and the All Fast marker is NOT
  //           yet done. During ARRIVAL the card is mounted on ArrivalPhase
  //           (Pass 2e §1.7); during UNDERWAY this gate is false so the
  //           marker is decorative.
  //
  // [Pass 2e LOCKED — anti-Pass-2b] `hasActiveOperation` is derived inline
  // here from `voyage.unifiedLog`, NOT lifted into a shared hook. Pass 2b's
  // reverted pattern over-extracted shared state into hooks that produced
  // TDZ regressions — keeping the derivation co-located avoids that class
  // of bug.
  // ────────────────────────────────────────────────────────────────────────
  // [Pass 2o] Shared predicate — same active notion as VoyageDocument.
  const hasActiveOperation = useMemo(() => {
    const log = voyage?.unifiedLog || [];
    const planItems = voyage?.voyagePlanItems || {};
    return findActiveSection(log, planItems, Date.now()) !== null;
  }, [voyage?.unifiedLog, voyage?.voyagePlanItems]);

  const preArrTappable = useMemo(
    () => voyage?.status === VOYAGE_STATUS.UNDERWAY
      && !phaseMarkerStatus.pre_arrival.done
      && !hasActiveOperation,
    [voyage?.status, phaseMarkerStatus.pre_arrival.done, hasActiveOperation]
  );

  const allFastTappable = useMemo(
    () => voyage?.status === VOYAGE_STATUS.ARRIVAL
      && !phaseMarkerStatus.all_fast.done,
    [voyage?.status, phaseMarkerStatus.all_fast.done]
  );

  // ────────────────────────────────────────────────────────────────────────
  // Pass 2a §1.3 — combined render list: leading phase markers, admin items,
  // trailing phase markers. Interleaving is structural (driven by
  // `placement: 'leading' | 'trailing'`), not data-driven.
  // ────────────────────────────────────────────────────────────────────────
  const itemsWithPhaseMarkers = useMemo(() => {
    const leading = PHASE_MARKERS.filter(m => m.placement === 'leading');
    const trailing = PHASE_MARKERS.filter(m => m.placement === 'trailing');
    return [...leading, ...renderableItems, ...trailing];
  }, [renderableItems]);

  // ────────────────────────────────────────────────────────────────────────
  // Counter — RENDER-ZERO SAFE: template string interpolates numbers, no
  // numeric `&&` short-circuit. See CODING-BRIEF-C §5.3.
  //
  // Pass 2a §1.4 — phase markers DO NOT count toward header counter (LOCKED).
  // When totalItems === 0, hide the counter entirely via ternary (NOT &&)
  // per `[v3 FIX R2-C1]` — prevents the bare "0 of 0" pattern from POB
  // hotfix cc4932a.
  // ────────────────────────────────────────────────────────────────────────
  const totalItems = renderableItems.length;
  const doneItems = useMemo(
    () => renderableItems.filter(it => {
      const s = derivedStatusByItemId[it.id];
      return s === 'completed' || s === 'skipped';
    }).length,
    [renderableItems, derivedStatusByItemId]
  );
  const headerText = totalItems > 0
    ? `Voyage Plan — ${doneItems} of ${totalItems} done`
    : `Voyage Plan`;
  const allDone = totalItems > 0 && doneItems === totalItems;

  // ────────────────────────────────────────────────────────────────────────
  // Expanded / collapsed default — synthesis B9.
  // Phone viewport (<768px) → collapsed; iPad+ → expanded. State is intentionally
  // NOT persisted across voyage sessions.
  // ────────────────────────────────────────────────────────────────────────
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 768;
  });
  const toggleExpanded = useCallback(() => setIsExpanded(prev => !prev), []);

  // ────────────────────────────────────────────────────────────────────────
  // Operation-ref observer effect (synthesis §3.5 belt-and-braces).
  // When a plan-attached operation_complete entry appears in unifiedLog but the
  // persisted plan-item state hasn't caught up (rare concurrent race), forward
  // the completion to the persisted state. One useEffect handles every item.
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!voyage?.id) return;
    if (!onUpdatePlanItem) return;
    const log = voyage.unifiedLog || [];
    const persistedItems = voyage.voyagePlanItems || {};
    for (const it of renderableItems) {
      if (it.kind !== 'operation_ref') continue;
      const persistedStatus = persistedItems[it.id]?.status || 'pending';
      if (persistedStatus === 'completed' || persistedStatus === 'skipped') continue;
      const matched = log.find(e => e && e.type === 'operation_complete' && e.planItemId === it.id);
      if (matched) {
        onUpdatePlanItem(voyage.id, it.id, {
          status: 'completed',
          linkedLogEntryId: matched.id,
          resolvedAt: matched.timestamp || matched.eventTime || new Date().toISOString(),
        });
      }
    }
  }, [voyage?.id, voyage?.unifiedLog, voyage?.voyagePlanItems, renderableItems, onUpdatePlanItem]);

  // ────────────────────────────────────────────────────────────────────────
  // Skip modal + undo snackbar state.
  // ────────────────────────────────────────────────────────────────────────
  const [skipTargetItem, setSkipTargetItem] = useState(null);
  const [undoToast, setUndoToast] = useState(null); // { itemId, previousState, label } | null
  const undoTimerRef = useRef(null);

  const clearUndoTimer = useCallback(() => {
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  }, []);

  const startUndoToast = useCallback((toast) => {
    clearUndoTimer();
    setUndoToast(toast);
    undoTimerRef.current = setTimeout(() => {
      setUndoToast(prev => (prev && prev.itemId === toast.itemId ? null : prev));
      undoTimerRef.current = null;
    }, 5000);
  }, [clearUndoTimer]);

  // Cleanup on unmount.
  useEffect(() => () => clearUndoTimer(), [clearUndoTimer]);

  // ────────────────────────────────────────────────────────────────────────
  // Handlers.
  // ────────────────────────────────────────────────────────────────────────
  const handleTap = useCallback((item) => {
    const status = derivedStatusByItemId[item.id];
    if (status === 'completed' || status === 'skipped') return;

    if (item.kind === 'waypoint') {
      // Waypoint skips 'in_progress' (synthesis §3.5).
      onLogPositionForWaypoint?.(item);
      return;
    }

    // Pass 2t §G2 — block retap during in-flight window. Predicate reads
    // unifiedLog DIRECTLY (NOT derivedStatusByItemId — that's a stale closure
    // memo). If an unmatched section already exists for this planItemId,
    // refuse the new start, scroll to the plan-card row, and surface a toast
    // via the parent. Catches the bug-#039 forensic shape: user taps row again
    // because no scroll feedback appeared, creating duplicate sections.
    const log = voyage?.unifiedLog || [];
    const completedIds = new Set(
      log.filter(e => e?.type === 'operation_complete' && e.operationId).map(e => e.operationId)
    );
    const existing = log.find(e =>
      e?.type === 'section'
      && e.planItemId === item.id
      && !completedIds.has(e.operationId)
    );
    if (existing) {
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = document.querySelector(`[data-testid="plan-item-row-${item.id}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        });
      }
      onRefusedDuplicateTap?.({
        label: item.operationSnapshot?.name || item.displayLabel || 'Operation'
      });
      return;
    }

    // operation_ref — mark in_progress optimistically (synthesis §3.5),
    // then open the operation modal via the parent. If the user cancels
    // without completing, status stays 'in_progress' (acceptable v1; they
    // can re-tap or skip).
    if (status === 'pending') {
      onUpdatePlanItem?.(voyage.id, item.id, { status: 'in_progress' });
    }
    onStartOperationForPlanItem?.(item);
  }, [
    derivedStatusByItemId, onLogPositionForWaypoint, onUpdatePlanItem,
    onStartOperationForPlanItem, voyage?.id, voyage?.unifiedLog, onRefusedDuplicateTap
  ]);

  const handleRequestSkip = useCallback((item) => {
    setSkipTargetItem(item);
  }, []);

  const handleSkipCancel = useCallback(() => {
    setSkipTargetItem(null);
  }, []);

  const handleSkipConfirm = useCallback(async (reason) => {
    if (!skipTargetItem) return;
    const item = skipTargetItem;
    const previousState = voyage.voyagePlanItems?.[item.id] || { status: 'pending' };
    const success = await onSkipPlanItem?.(voyage.id, item.id, reason);
    setSkipTargetItem(null);
    if (success) {
      const label = item.kind === 'waypoint'
        ? (item.label || 'Waypoint')
        : (item.operationSnapshot?.name || item.displayLabel || 'Operation');
      startUndoToast({
        itemId: item.id,
        previousState,
        message: `Skipped: ${label}`
      });
    }
  }, [skipTargetItem, voyage?.id, voyage?.voyagePlanItems, onSkipPlanItem, startUndoToast]);

  const handleUndo = useCallback(() => {
    if (!undoToast) return;
    const { itemId, previousState } = undoToast;
    // Revert by writing the previous state shape back. Audit log entries are
    // NOT unwound (regulatory — the skip event happened); only the per-item
    // state map flips back.
    onUpdatePlanItem?.(voyage.id, itemId, {
      status: previousState?.status || 'pending',
      skipReason: previousState?.skipReason,
      resolvedAt: previousState?.resolvedAt,
      resolvedBy: previousState?.resolvedBy,
      resolvedByName: previousState?.resolvedByName,
      linkedLogEntryId: previousState?.linkedLogEntryId
    });
    setUndoToast(null);
    clearUndoTimer();
  }, [undoToast, voyage?.id, onUpdatePlanItem, clearUndoTimer]);

  const handleDismissUndo = useCallback(() => {
    setUndoToast(null);
    clearUndoTimer();
  }, [clearUndoTimer]);

  // Pass 2a §1.6 — Option A LOCKED: the previous early-return at
  // `renderableItems.length === 0` is REMOVED. The card now renders phase
  // markers even when admin's items are zero (filtered or authored empty).

  return (
    <>
      <section
        className="mx-3 sm:mx-4 mt-3 mb-2 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
        data-testid="voyage-plan-underway-card"
      >
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          className="w-full flex items-center gap-3 px-3 sm:px-4 py-3 min-h-[56px] bg-slate-50 hover:bg-slate-100 transition-colors"
          data-testid="voyage-plan-underway-card-header"
        >
          <ClipboardList className="h-5 w-5 text-slate-700 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0 text-left">
            <p className="text-sm font-semibold text-slate-900 truncate">{headerText}</p>
            <p className="text-xs text-slate-500 truncate">{snapshot.planName || 'Voyage Plan'}</p>
          </div>
          {allDone ? (
            <span
              className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700"
              data-testid="voyage-plan-all-done-badge"
            >
              All done
            </span>
          ) : null}
          {isExpanded
            ? <ChevronUp className="h-5 w-5 text-slate-500 flex-shrink-0" aria-hidden="true" />
            : <ChevronDown className="h-5 w-5 text-slate-500 flex-shrink-0" aria-hidden="true" />
          }
        </button>

        {isExpanded ? (
          <div data-testid="voyage-plan-underway-card-list">
            {itemsWithPhaseMarkers.map(item => {
              if (item.kind === 'phase') {
                // Pass 2e §1.3 — resolve the per-marker tap handler. Pre-Arr
                // and All Fast get a handler only when their gating booleans
                // are true AND the parent passed a callback. All other markers
                // (Pre-Dep, Let Go) explicitly resolve to undefined —
                // PhaseMarkerRow treats that as decorative.
                let onTapPhaseAction;
                if (item.id === '__phase_pre_arrival' && preArrTappable && onTapPreArrival) {
                  onTapPhaseAction = onTapPreArrival;
                } else if (item.id === '__phase_all_fast' && allFastTappable && onTapAllFast) {
                  onTapPhaseAction = onTapAllFast;
                }
                return (
                  <PhaseMarkerRow
                    key={item.id}
                    id={item.id}
                    label={item.label}
                    done={phaseMarkerStatus[item.id.replace('__phase_', '')]?.done || false}
                    resolvedAt={phaseMarkerStatus[item.id.replace('__phase_', '')]?.resolvedAt || null}
                    vesselTimezone={vesselTimezone}
                    voyage={voyage}
                    onTapPhaseAction={onTapPhaseAction}
                  />
                );
              }
              return (
                <PlanItemRow
                  key={item.id}
                  item={item}
                  voyage={voyage}
                  status={derivedStatusByItemId[item.id] || 'pending'}
                  isReadOnly={isReadOnly}
                  onTap={handleTap}
                  onRequestSkip={handleRequestSkip}
                  completedAt={voyage.voyagePlanItems?.[item.id]?.resolvedAt || null}
                  vesselTimezone={vesselTimezone}
                />
              );
            })}
          </div>
        ) : null}
      </section>

      <SinglePlanItemSkipModal
        isOpen={!!skipTargetItem}
        item={skipTargetItem}
        onCancel={handleSkipCancel}
        onConfirm={handleSkipConfirm}
      />

      {undoToast ? (
        <UndoSnackbar
          message={undoToast.message}
          onUndo={handleUndo}
          onDismiss={handleDismissUndo}
        />
      ) : null}
    </>
  );
};

export default VoyagePlanUnderwayCard;
// Named exports for testing/co-location.
export { VoyagePlanInlineBanner, SinglePlanItemSkipModal, UndoSnackbar, PlanItemRow, PhaseMarkerRow };
